import {
  ImpersonationExchangeError,
  useImpersonationActions,
} from '@ox-technologies/auth';
import { Button, Combobox, ComboboxOption, Modal } from '@ox-technologies/ui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from 'utils/get-token/get-token';
import {
  SearchedUser,
  useUserSearch,
} from 'utils/impersonation/use-user-search';

/**
 * Admin-only dialog picking the user whose app directory to view. The exchange
 * itself lives in @ox-technologies/auth; the identity server decides whether the target
 * may be impersonated at all (platform admins are refused), so a rejected pick
 * surfaces as the error below rather than being filtered out of the list here.
 */
const ImpersonationStartModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  // getToken so the exchange also works behind the CUSTOM_AUTH_TOKEN dev bypass.
  const { startImpersonation } = useImpersonationActions({
    getSubjectToken: getToken,
  });
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<SearchedUser | null>(null);
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { users, isFetching, isError, needsFullEmail } = useUserSearch(
    search,
    open
  );

  // Selecting an option makes the Combobox echo its label into the input, which
  // is no longer a whole e-mail — so the lookup returns nothing and the results
  // would drop the selected user, leaving the trigger blank. Keep it pinned.
  const options = useMemo<ComboboxOption[]>(() => {
    const found = users.map((user) => ({
      label: describeUser(user),
      value: user.id,
    }));

    return selectedUser && !users.some((user) => user.id === selectedUser.id)
      ? [
          { label: describeUser(selectedUser), value: selectedUser.id },
          ...found,
        ]
      : found;
  }, [users, selectedUser]);

  const close = () => {
    setSearch('');
    setSelectedUser(null);
    setError(undefined);
    onClose();
  };

  const submit = async () => {
    if (!selectedUser) {
      setError(t('Impersonation.Start.Required'));
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      await startImpersonation(selectedUser.id);
      close();
    } catch (err: unknown) {
      // Surface the identity server's own error — an Error stringifies to `{}`
      // and the OAuth error is the only thing explaining a failed exchange.
      console.error(
        'Failed to start impersonation:',
        err instanceof ImpersonationExchangeError
          ? `${err.message} — ${err.body}`
          : err
      );
      setError(t('Impersonation.Start.Failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('Impersonation.Start.Title')}
      description={t('Impersonation.Start.Description')}
      testId="impersonation-start-modal"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={close}
            testId="impersonation-start-cancel"
          >
            {t('Impersonation.Start.Cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={isSubmitting || !selectedUser}
            testId="impersonation-start-confirm"
          >
            {t('Impersonation.Start.Confirm')}
          </Button>
        </>
      }
    >
      <Combobox
        label={t('Impersonation.Start.UserLabel')}
        placeholder={t('Impersonation.Start.UserPlaceholder')}
        helperText={t('Impersonation.Start.UserHelper')}
        options={options}
        value={selectedUser?.id ?? null}
        onValueChange={(value) => {
          setSelectedUser(
            users.find((user) => user.id === value) ??
              (value === selectedUser?.id ? selectedUser : null)
          );
          setError(undefined);
        }}
        onInputValueChange={setSearch}
        loading={isFetching}
        emptyMessage={
          isError
            ? t('Impersonation.Start.SearchError')
            : needsFullEmail
              ? t('Impersonation.Start.TypeFullEmail')
              : t('Impersonation.Start.NoUsers')
        }
        error={error}
        testId="impersonation-start-user"
      />
    </Modal>
  );
};

/** "Jana Testová (jana@example.com)", degrading to whatever the user has. */
const describeUser = ({ email, firstName, lastName }: SearchedUser) => {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();

  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

export default ImpersonationStartModal;
